import React from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Box, Button, Card, CardContent, Chip, Divider, Grid, Paper, Typography} from "@mui/material";
import {Campaign, EmojiEvents, Redeem, TrendingUp} from "@mui/icons-material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CircularLoading from "../../components/CircularLoading";
import {AWARD_TYPE} from "../../constants/awardType";
import {
    useGetArticles,
    useGetSettings,
    useGetShops,
    useGetVendorCoupons,
    useGetVendorWallet
} from "../../services/ContentManager";
import {
    ARTICLE_LIST_ROUTE,
    COUPON_LIST_ROUTE,
    COUPON_REDEEM_ROUTE,
    SPONSORED_NOTIFICATIONS_ROUTE,
    WALLET_ROUTE
} from "../../constants/vendors";

// Allineato a DEFAULT_COST_PER_READ di SponsoredNotificationService, come in Wallet.
const COST_PER_READ = 20;

const MOVEMENT_LABELS = {
    SALE: 'movementSale',
    ARTICLE_SPONSORSHIP: 'movementArticleSponsorship',
    NOTIFICATION_READ: 'movementNotificationRead'
};

/** Movimenti del mese corrente che hanno portato punti: sono i riscatti in negozio. */
function earnedThisMonth(movements) {
    const now = new Date();
    return (movements || [])
        .filter(m => m.type === 'SALE')
        .filter(m => {
            const d = new Date(m.date);
            return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        })
        .reduce((acc, m) => ({punti: acc.punti + parseFloat(m.amount || 0), riscatti: acc.riscatti + 1}),
            {punti: 0, riscatti: 0});
}

/** Somma il valore in denaro di una lista di coupon. */
function sommaValore(coupons) {
    return coupons.reduce((acc, c) => acc + parseFloat(c.value || 0), 0);
}

/** Riquadro numerico che porta alla pagina corrispondente. */
function StatCard({to, icon, value, label, hint, hintColor}) {
    return (
        <Card sx={{height: '100%'}}>
            <CardContent component={Link} to={to}
                         sx={{display: 'block', textDecoration: 'none', color: 'inherit', height: '100%'}}>
                <Box color="primary.main" mb={0.5}>{icon}</Box>
                <Typography variant="h4" fontWeight={700} lineHeight={1.1}>{value}</Typography>
                <Typography variant="body2" fontWeight={500}>{label}</Typography>
                {hint && <Typography variant="caption" color={hintColor || 'textSecondary'}>{hint}</Typography>}
            </CardContent>
        </Card>
    );
}

/** Una delle due cose su cui i punti si spendono, con il prezzo accanto. */
function SpendCard({icon, title, cost, explain, action, to}) {
    return (
        <Card variant="outlined" sx={{height: '100%'}}>
            <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 1, height: '100%'}}>
                <Box display="flex" alignItems="center" gap={1.2}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                        bgcolor: 'primary.light', color: 'primary.main',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>{icon}</Box>
                    <Box minWidth={0}>
                        <Typography fontWeight={700}>{title}</Typography>
                        <Typography variant="body2" color="textSecondary">{cost}</Typography>
                    </Box>
                </Box>
                <Typography variant="body2" color="textSecondary">{explain}</Typography>
                <Button component={Link} to={to} variant="contained" sx={{alignSelf: 'flex-start', mt: 'auto'}}>
                    {action}
                </Button>
            </CardContent>
        </Card>
    );
}

/** Una delle due facce del rimborso voucher: quanto e' ancora dovuto, quanto e' gia' arrivato. */
function RefundFigure({label, amount, explain, color}) {
    const {t} = useTranslation();
    return (
        <Box>
            <Typography variant="body2" fontWeight={700}>{label}</Typography>
            <Typography variant="h4" fontWeight={700} color={color} lineHeight={1.2}>
                {amount.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} {t('currency')}
            </Typography>
            <Typography variant="caption" color="textSecondary">{explain}</Typography>
        </Box>
    );
}

/**
 * Pagina di atterraggio del negoziante.
 * Parte dai punti — quanti sono, da dove arrivano e come si spendono — perche'
 * prima erano raggiungibili solo entrando nel portafoglio, che per giunta non
 * aveva nemmeno una voce di menu. Sotto, l'unica azione che si fa ogni giorno.
 */
export default function VendorHome({vendor}) {
    const {t} = useTranslation();
    const uid = vendor?.uid;

    const {wallet, status} = useGetVendorWallet(uid);
    const {shops} = useGetShops(uid);
    const {articles} = useGetArticles(uid);
    const {coupons} = useGetVendorCoupons(uid);
    const {settings} = useGetSettings();

    if (status === 'loading') return <CircularLoading/>;

    const balance = wallet?.balance != null ? parseFloat(wallet.balance) : 0;
    const movements = wallet?.movements || [];
    const mese = earnedThisMonth(movements);
    const visualizationCost = settings?.find(s => s.name === 'advertisement_visualization_cost')?.value ?? '-';

    // I voucher sono i coupon SHOP: denaro che l'esercente ha anticipato al cliente e che
    // Pin Bike gli restituisce. Arrivano qui solo da riscattati, perche' il legame col negozio
    // nasce nella redeem, quindi l'unica cosa che li distingue e' se il rimborso e' avvenuto.
    const voucher = (coupons || []).filter(c => c.awardType === AWARD_TYPE.SHOP);
    const daRimborsare = sommaValore(voucher.filter(c => !c.refundDate));
    const rimborsati = sommaValore(voucher.filter(c => c.refundDate));
    // Premi community comprati coi punti e non ancora ritirati al banco: e' l'unico
    // coupon che l'esercente vede prima del riscatto, perche' nasce gia' legato al negozio.
    const daRitirare = (coupons || []).filter(c => c.awardType === AWARD_TYPE.COMMUNITY && !c.redemptionDate);

    const visibili = (articles || []).filter(a => a.visible).length;
    const nascosti = (articles || []).length - visibili;

    return (
        <Box>
            <Box mb={3}>
                <Typography variant="h4">{vendor?.businessName || vendor?.username || ''}</Typography>
                <Typography variant="body2" color="textSecondary">
                    {t('shops')}: {(shops || []).length}
                </Typography>
            </Box>

            {/* --- i punti, e cosa farci --- */}
            <Paper sx={{display: 'flex', flexWrap: 'wrap', overflow: 'hidden', mb: 3}}>
                <Box sx={{
                    width: {xs: '100%', md: 340}, flexShrink: 0,
                    bgcolor: 'secondary.main', color: 'common.white', p: 3
                }}>
                    <Typography variant="overline" sx={{opacity: .75}}>
                        {t('vendorYourPoints')}
                    </Typography>
                    <Box display="flex" alignItems="baseline" gap={1}>
                        <Typography variant="h2" fontWeight={700} lineHeight={1}>
                            {balance.toLocaleString('it-IT')}
                        </Typography>
                        <Typography sx={{opacity: .8}}>{t('points', {count: balance})}</Typography>
                    </Box>
                    {mese.riscatti > 0 &&
                        <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                            <Chip size="small" icon={<TrendingUp/>} label={mese.punti.toLocaleString('it-IT')}
                                  sx={{bgcolor: 'green.main', color: '#16250a', fontWeight: 700}}/>
                            <Typography variant="body2" sx={{opacity: .85}}>
                                {t('vendorEarnedThisMonth', {n: mese.riscatti})}
                            </Typography>
                        </Box>
                    }
                    <Typography variant="body2" sx={{opacity: .75, mt: 2}}>
                        {t('walletExplain')}
                    </Typography>
                </Box>

                <Box sx={{flexGrow: 1, minWidth: 300, p: 3}}>
                    <Typography variant="h6" fontWeight={700}>{t('vendorHowToSpend')}</Typography>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        {t('vendorHowToSpendExplain')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <SpendCard
                                icon={<EmojiEvents/>}
                                title={t('advertisedArticles')}
                                cost={t('vendorAdvertisedCost', {count: Number(visualizationCost) || 0})}
                                explain={t('vendorVisibilityExplain')}
                                action={t('advertisedArticles')}
                                to={ARTICLE_LIST_ROUTE}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SpendCard
                                icon={<Campaign/>}
                                title={t('sponsoredNotifications')}
                                cost={t('vendorNotificationCost', {count: COST_PER_READ})}
                                explain={t('vendorNotificationExplain')}
                                action={t('sponsoredNotifications')}
                                to={SPONSORED_NOTIFICATIONS_ROUTE}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* --- l'azione quotidiana, staccata dal resto --- */}
            <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={4}>
                    <Card sx={{height: '100%', borderTop: 4, borderColor: 'primary.main'}}>
                        <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%'}}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: 'primary.main', color: 'common.white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}><QrCodeScannerIcon/></Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>{t('couponRedeem')}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t('vendorCustomerAtCounter')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                {t('vendorRedeemExplain')}
                            </Typography>
                            <Box component="ul" sx={{m: 0, pl: 2.5, color: 'text.secondary', typography: 'body2'}}>
                                <li>{t('vendorVoucherSummary')}</li>
                                <li>{t('vendorCommunityPrizeSummary')}</li>
                            </Box>
                            <Button component={Link} to={COUPON_REDEEM_ROUTE} variant="contained" size="large"
                                    fullWidth sx={{mt: 'auto'}}>
                                {t('couponRedeem')}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Grid container spacing={3} sx={{height: '100%'}}>
                        <Grid item xs={12} sm={6}>
                            <StatCard
                                to={ARTICLE_LIST_ROUTE}
                                icon={<EmojiEvents/>}
                                value={visibili}
                                label={t('vendorActivePrizes')}
                                hint={nascosti > 0
                                    ? t('vendorHiddenPrizes', {n: nascosti})
                                    : null}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StatCard
                                to={COUPON_REDEEM_ROUTE}
                                icon={<Redeem/>}
                                value={daRitirare.length}
                                label={t('vendorPrizesToCollect')}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* --- il denaro anticipato ai clienti e il suo rimborso --- */}
            <Paper sx={{p: 3, mb: 3}}>
                <Box display="flex" alignItems="baseline" mb={1}>
                    <Typography variant="h6" fontWeight={700}>{t('voucher')}</Typography>
                    <Button component={Link} to={COUPON_LIST_ROUTE} size="small" sx={{ml: 'auto'}}>
                        {t('seeAll')}
                    </Button>
                </Box>
                <Typography variant="body2" color="textSecondary" mb={2}>
                    {t('voucherDescription')}
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <RefundFigure
                            label={t('toRefunded')}
                            amount={daRimborsare}
                            color="primary.main"
                            explain={t('vendorToRefundInfo')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <RefundFigure
                            label={t('refunded')}
                            amount={rimborsati}
                            color="green.main"
                            explain={t('vendorAllRefundedInfo')}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* --- da dove arrivano e dove vanno i punti --- */}
            <Paper sx={{p: 3}}>
                <Box display="flex" alignItems="baseline" mb={1}>
                    <Typography variant="h6" fontWeight={700}>{t('vendorLastMovements')}</Typography>
                    <Button component={Link} to={WALLET_ROUTE} size="small" sx={{ml: 'auto'}}>
                        {t('seeAll')}
                    </Button>
                </Box>
                {movements.length === 0
                    ? <Typography color="textSecondary">{t('walletNoMovements')}</Typography>
                    : movements.slice(0, 4).map((m, i) => {
                        const amount = parseFloat(m.amount || 0);
                        const key = MOVEMENT_LABELS[m.type] || m.type;
                        return (
                            <Box key={i}>
                                {i > 0 && <Divider/>}
                                <Box display="flex" alignItems="center" gap={2} py={1.2}>
                                    <Box minWidth={0} flexGrow={1}>
                                        <Typography variant="body2" noWrap>
                                            {t(key)}{m.description ? ` · ${m.description}` : ''}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {new Date(m.date).toLocaleDateString('it-IT')}
                                        </Typography>
                                    </Box>
                                    <Typography fontWeight={700} flexShrink={0}
                                                color={amount >= 0 ? 'green.main' : 'primary.main'}>
                                        {amount >= 0 ? '+' : ''}{amount.toLocaleString('it-IT')}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })
                }
            </Paper>
        </Box>
    );
}
